import { useEffect, useState } from 'react';
import useScimAuditLogsQuery from '../../queries/useScimAuditLogsQuery';
import { ScimAuditLogAction } from '../../models/ScimAuditLog';

import './ScimSyncHistory.scss';

const COLLAPSED_COUNT = 4;

const STATUS_LABELS: Record<ScimAuditLogAction, string> = {
	provisioned: 'Provisioned',
	updated: 'Updated',
	deactivated: 'Deactivated',
	reactivated: 'Reactivated',
	provision_failed: 'Provisioned Failed',
	update_failed: 'Update Failed',
	deactivation_failed: 'Deactivation Failed'
};

const formatEventDate = ( isoDate: string ) => new Intl.DateTimeFormat(
	'en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }
).format( new Date( isoDate ) );

type Props = {
	workspaceID: number;
};

const ScimSyncHistory = ( { workspaceID }: Props ) => {
	const [ expanded, setExpanded ] = useState( false );
	const {
		data: logs, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage
	} = useScimAuditLogsQuery( { workspaceID } );

	useEffect( () => {
		if ( expanded && hasNextPage && !isFetchingNextPage ) fetchNextPage();
	}, [ expanded, hasNextPage, isFetchingNextPage, fetchNextPage ] );

	if ( isLoading ) return null;

	const allLogs = logs ?? [];
	const visibleLogs = expanded ? allLogs : allLogs.slice( 0, COLLAPSED_COUNT );
	const showSeeMore = !expanded && ( allLogs.length > COLLAPSED_COUNT || !!hasNextPage );

	return (
		<div className="ScimSyncHistory">
			<h2 className="ScimSyncHistory-title">Sync History</h2>

			{allLogs.length === 0 ? (
				<p className="ScimSyncHistory-empty">
					No sync events yet. Once your IdP starts syncing, events will appear here.
				</p>
			) : (
				<>
					<div className="ScimSyncHistory-table">
						<div className="ScimSyncHistory-header">
							<span>User</span>
							<span>Date Provisioned</span>
							<span>Status</span>
						</div>
						{visibleLogs.map( log => (
							<div className="ScimSyncHistory-row" key={log.id}>
								<span className="ScimSyncHistory-cell">{log.userEmail}</span>
								<span className="ScimSyncHistory-cell">{formatEventDate( log.createdAt )}</span>
								<span className={`ScimSyncHistory-status ScimSyncHistory-status--${log.action}`}>
									{STATUS_LABELS[ log.action ]}
								</span>
							</div>
						) )}
					</div>

					{showSeeMore && (
						<button
							className="ScimSyncHistory-see-more"
							onClick={() => setExpanded( true )}
						>
							See More
						</button>
					)}
				</>
			)}
		</div>
	);
};

export default ScimSyncHistory;
